// Stock Taking service (operator scope).
//
// Schemas 對齊 app/controllers/api/operator/v1/stock_takings_controller.rb +
// stock_taking_shelves_controller.rb 的實際 render block — 不是 BACKEND.md 文件。
import { z } from 'zod';

import { operatorApiFetch } from './apiService';
import { type ApiErrorEnvelope, apiList, apiSuccess, parseApiResponse } from '@services/apiSchemas';
import {
  PreviewInventoryChangesResponseSchema,
} from '@services/inventoryConflictSchemas';

// ===== Shared shapes =====

const JobIdSchema = z.object({ job_id: z.string() }).passthrough();

const BatchResultRowSchema = z.object({
  id: z.string(),
  success: z.boolean(),
  message: z.string().optional(),
  interpolations: z.record(z.string(), z.unknown()).optional(),
}).passthrough();

// ===== Per-endpoint schemas =====

// `serialize_stock_taking` (controller#serialize_stock_taking) — 共用於 show / get_records / create / update / approve / reject
const StockTakingSchema = z.object({
  id: z.string(),
  name: z.string(),
  warehouse_id: z.string(),
  warehouse_name: z.string().nullable(),
  status: z.string(),
  mode: z.string(),
  snapshot_status: z.string().nullable().optional(),
  snapshot_ready: z.boolean(),
  is_blind: z.boolean(),
  finalized_by_id: z.string().nullable(),
  finalized_by_name: z.string().nullable(),
  note: z.string().nullable(),
  total_shelves: z.number(),
  completed_shelves: z.number(),
  finalized_at: z.string().nullable(),
  created_at: z.string(),
  editable: z.boolean(),
  deletable: z.boolean(),
  approvable: z.boolean(),
  rejectable: z.boolean(),
}).passthrough();

// preview_split
const PreviewSplitGroupSchema = z.object({
  zone_id: z.string().nullable().optional(),
  label: z.string().nullable(),
  batch_index: z.number().optional(),
  shelf_count: z.number(),
}).passthrough();
const PreviewSplitDataSchema = z.object({
  groups: z.array(PreviewSplitGroupSchema),
  total_groups: z.number(),
}).passthrough();

// batch_create
const BatchCreatedItemSchema = z.object({ id: z.string(), name: z.string() }).passthrough();
const BatchCreateDataSchema = z.object({
  stock_takings: z.array(BatchCreatedItemSchema),
  count: z.number(),
}).passthrough();

// suggest_name
const SuggestNameSchema = z.object({ name: z.string() }).passthrough();

// virtual_preview — inventory row
const VirtualPreviewRowSchema = z.object({
  id: z.string(),
  inventory_id: z.string(),
  sku: z.string().nullable(),
  product_name: z.string().nullable(),
  on_hand: z.number(),
  batch: z.string().nullable(),
  expiration_date: z.string().nullable(),
  inventory_type_id: z.string().nullable(),
  inventory_type_name: z.string().nullable(),
}).passthrough();

// generate_for_virtual
const GenerateForVirtualSchema = z.object({ id: z.string(), name: z.string() }).passthrough();

// items endpoint (stock_takings/:id/items)
const StockTakingItemRowSchema = z.object({
  id: z.string(),
  inventory_id: z.string(),
  shelf_id: z.string(),
  shelf_name: z.string().nullable(),
  product_name: z.string().nullable(),
  sku: z.string().nullable(),
  inventory_type_name: z.string().nullable(),
  batch: z.string().nullable(),
  expiration_date: z.string().nullable(),
  custom_attributes: z.record(z.string(), z.unknown()).nullable(),
  expected_quantity: z.number().nullable(),
  counted_quantity: z.number().nullable(),
  difference: z.number().nullable(),
  note: z.string().nullable(),
  counted_at: z.string().nullable(),
  count_entries_count: z.number(),
}).passthrough();

// item_count_entries
const CountEntrySchema = z.object({
  id: z.string(),
  counted_quantity: z.number(),
  expected_quantity: z.number(),
  difference: z.number(),
  operator_id: z.string().nullable(),
  operator_name: z.string().nullable(),
  counted_at: z.string(),
  note: z.string().nullable(),
}).passthrough();
const CountEntriesDataSchema = z.object({
  item_count_entries: z.array(CountEntrySchema),
}).passthrough();

// batch_update_items result
const BatchUpdateResultSchema = z.object({
  results: z.array(BatchResultRowSchema),
}).passthrough();

// zero_out_uncounted
const ZeroOutSchema = z.object({ zeroed_count: z.number() }).passthrough();

// stock_taking_shelves index (shelves)
const StockTakingShelfRowSchema = z.object({
  id: z.string(),
  shelf_id: z.string(),
  shelf_name: z.string().nullable(),
  status: z.string(),
}).passthrough();

// stock_taking_shelves show — items + reference_inventories
const ShelfItemSchema = z.object({
  id: z.string(),
  inventory_id: z.string(),
  product_name: z.string().nullable(),
  sku: z.string().nullable(),
  inventory_type_name: z.string().nullable(),
  expected_quantity: z.number().nullable(),
  counted_quantity: z.number().nullable(),
  note: z.string().nullable(),
  counted_at: z.string().nullable(),
}).passthrough();
const ReferenceInventorySchema = z.object({
  inventory_id: z.string(),
  product_name: z.string().nullable(),
  sku: z.string().nullable(),
  inventory_type_name: z.string().nullable(),
  quantity: z.number(),
}).passthrough();
const ShelfDetailSchema = z.object({
  id: z.string(),
  status: z.string(),
  shelf_name: z.string().nullable(),
  mode: z.string(),
  // Static mode 下 BE 回空陣列 + items_total；items 透過 getShelfItems 分批拉。
  // Dynamic mode 維持回完整 items + reference_inventories（資料量受 App 寫入節奏限制）。
  items: z.array(ShelfItemSchema),
  reference_inventories: z.array(ReferenceInventorySchema),
  items_total: z.number().optional(),
}).passthrough();

const ShelfItemsPageSchema = z.object({
  items: z.array(ShelfItemSchema),
  next_cursor: z.string().nullable(),
  total: z.number().optional(), // 僅首頁回（cursor 為空時）
}).passthrough();

// get_ids — render_data_item(array)
const IdsDataSchema = z.array(z.string());

export type StockTaking = z.infer<typeof StockTakingSchema>;
export type StockTakingItemRow = z.infer<typeof StockTakingItemRowSchema>;
export type StockTakingShelfRow = z.infer<typeof StockTakingShelfRowSchema>;
export type ShelfDetail = z.infer<typeof ShelfDetailSchema>;
export type ShelfItem = z.infer<typeof ShelfItemSchema>;
export type ShelfItemsPage = z.infer<typeof ShelfItemsPageSchema>;
export type CountEntry = z.infer<typeof CountEntrySchema>;

// ===== Service =====

interface BatchUpdateItemPayload {
  id: string;
  counted_quantity?: number | string | null;
  note?: string | null;
}

interface ShelfConfirmItemPayload {
  inventory_id: string;
  counted_quantity?: number | null;
  note?: string | null;
}

interface VirtualScopePreviewInput {
  merchantId: string;
  filter?: Record<string, unknown>;
  pageSize?: number;
  sinceId?: string | null;
}

export const stockTakingService = {
  // 預覽切分
  previewSplit: async (data: Record<string, unknown>) => {
    const res = await operatorApiFetch('stock_takings/preview_split', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return parseApiResponse(res, apiSuccess(PreviewSplitDataSchema));
  },

  // 批次建立盤點單
  batchCreate: async (data: Record<string, unknown>) => {
    const res = await operatorApiFetch('stock_takings/batch_create', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return parseApiResponse(res, apiSuccess(BatchCreateDataSchema));
  },

  // 列表查詢
  getRecords: async (params: Record<string, unknown>) => {
    const res = await operatorApiFetch('stock_takings/get_records', {
      method: 'POST',
      body: JSON.stringify(params),
    });
    return parseApiResponse(res, apiList(StockTakingSchema));
  },

  // 全選 IDs
  getIds: async (params: Record<string, unknown>) => {
    const res = await operatorApiFetch('stock_takings/get_ids', {
      method: 'POST',
      body: JSON.stringify(params),
    });
    return parseApiResponse(res, apiSuccess(IdsDataSchema));
  },

  // 匯出
  export: async (ids: string[], params: Record<string, unknown> = {}) => {
    const res = await operatorApiFetch('stock_takings/export', {
      method: 'POST',
      body: JSON.stringify({ ids, ...params }),
    });
    return parseApiResponse(res, apiSuccess(JobIdSchema));
  },

  // 取得單筆
  get: async (id: string) => {
    const res = await operatorApiFetch(`stock_takings/${id}`);
    return parseApiResponse(res, apiSuccess(StockTakingSchema));
  },

  // 建立盤點單
  create: async (data: Record<string, unknown>) => {
    const res = await operatorApiFetch('stock_takings', {
      method: 'POST',
      body: JSON.stringify({ stock_taking: data }),
    });
    return parseApiResponse(res, apiSuccess(StockTakingSchema));
  },

  // 更新盤點單
  update: async (id: string, data: Record<string, unknown>) => {
    const res = await operatorApiFetch(`stock_takings/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ stock_taking: data }),
    });
    return parseApiResponse(res, apiSuccess(StockTakingSchema));
  },

  // 刪除盤點單 — render_data_item(nil)，data 為 null
  delete: async (id: string) => {
    const res = await operatorApiFetch(`stock_takings/${id}`, {
      method: 'DELETE',
    });
    return parseApiResponse(res, apiSuccess(z.null()));
  },

  // 預覽結算（檢查庫存衝突）
  previewApprove: async (id: string) => {
    const res = await operatorApiFetch(`stock_takings/${id}/preview_approve`, {
      method: 'POST',
    });
    return parseApiResponse(res, apiSuccess(PreviewInventoryChangesResponseSchema));
  },

  // 結算盤點（套用盤盈盤虧到庫存）— conflict / race 由 BE raise
  // ADJUSTMENT_EXCEEDS_AVAILABLE 經 ApiErrorEnvelope 回。
  approve: async (id: string) => {
    const res = await operatorApiFetch(`stock_takings/${id}/approve`, {
      method: 'POST',
    });
    return parseApiResponse(res, apiSuccess(StockTakingSchema));
  },

  // 拒絕盤點（軟取消，保留稽核軌跡，不調整庫存）
  reject: async (id: string) => {
    const res = await operatorApiFetch(`stock_takings/${id}/reject`, {
      method: 'POST',
    });
    return parseApiResponse(res, apiSuccess(StockTakingSchema));
  },

  // 取得某個 item 的盤點歷史（append-only count entries，多輪盤點）
  getItemCountEntries: async (stockTakingId: string, itemId: string) => {
    const res = await operatorApiFetch(
      `stock_takings/${stockTakingId}/items/${itemId}/count_entries`
    );
    return parseApiResponse(res, apiSuccess(CountEntriesDataSchema));
  },

  // 取得儲位列表（分頁）
  getShelves: async (stockTakingId: string, params: Record<string, string | number> = {}) => {
    const query = new URLSearchParams(
      Object.entries(params).map(([k, v]) => [k, String(v)])
    ).toString();
    const url = query
      ? `stock_takings/${stockTakingId}/shelves?${query}`
      : `stock_takings/${stockTakingId}/shelves`;
    const res = await operatorApiFetch(url);
    return parseApiResponse(res, apiList(StockTakingShelfRowSchema));
  },

  // 取得儲位詳情
  getShelf: async (stockTakingId: string, shelfId: string) => {
    const res = await operatorApiFetch(`stock_takings/${stockTakingId}/shelves/${shelfId}`);
    return parseApiResponse(res, apiSuccess(ShelfDetailSchema));
  },

  // Static mode 分批拉 items（cursor paging by stock_taking_item.id, UUIDv7）。
  // 前端 useShelfItemsLoader 連續呼叫到 next_cursor === null；單批失敗可從 lastCursor retry。
  getShelfItems: async (
    stockTakingId: string,
    shelfId: string,
    { cursor, pageSize }: { cursor?: string | null; pageSize?: number } = {}
  ) => {
    const qs = new URLSearchParams();
    if (cursor) qs.set('cursor', cursor);
    if (pageSize) qs.set('page_size', String(pageSize));
    const query = qs.toString();
    const url = query
      ? `stock_takings/${stockTakingId}/shelves/${shelfId}/items?${query}`
      : `stock_takings/${stockTakingId}/shelves/${shelfId}/items`;
    const res = await operatorApiFetch(url);
    return parseApiResponse(res, apiSuccess(ShelfItemsPageSchema));
  },

  // 提交儲位盤點結果 — render_data_item(nil)
  confirmShelf: async (stockTakingId: string, shelfId: string, items: ShelfConfirmItemPayload[]) => {
    const res = await operatorApiFetch(
      `stock_takings/${stockTakingId}/shelves/${shelfId}/confirm`,
      { method: 'POST', body: JSON.stringify({ items }) }
    );
    return parseApiResponse(res, apiSuccess(z.null()));
  },

  // 取得盤點項目列表（靜態模式用）
  getItems: async (stockTakingId: string, params: Record<string, unknown>) => {
    const res = await operatorApiFetch(`stock_takings/${stockTakingId}/items`, {
      method: 'POST',
      body: JSON.stringify(params),
    });
    return parseApiResponse(res, apiList(StockTakingItemRowSchema));
  },

  // 匯出盤點項目
  exportItems: async (stockTakingId: string) => {
    const res = await operatorApiFetch(`stock_takings/${stockTakingId}/export_items`, {
      method: 'POST',
    });
    return parseApiResponse(res, apiSuccess(JobIdSchema));
  },

  // 匯入盤點項目
  importItems: async (stockTakingId: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await operatorApiFetch(`stock_takings/${stockTakingId}/import_items`, {
      method: 'POST',
      body: formData,
    });
    return parseApiResponse(res, apiSuccess(JobIdSchema));
  },

  // 批次更新 stock_taking_items
  batchUpdateItems: async (stockTakingId: string, items: BatchUpdateItemPayload[]) => {
    const res = await operatorApiFetch(`stock_takings/${stockTakingId}/batch_update_items`, {
      method: 'PATCH',
      body: JSON.stringify({ items }),
    });
    return parseApiResponse(res, apiSuccess(BatchUpdateResultSchema));
  },

  // 確認所有儲位 — render_data_item(nil)
  confirmAllShelves: async (stockTakingId: string) => {
    const res = await operatorApiFetch(`stock_takings/${stockTakingId}/confirm_all_shelves`, {
      method: 'POST',
    });
    return parseApiResponse(res, apiSuccess(z.null()));
  },

  // 將未盤項目 (counted_quantity = null) 批次標記為 0
  zeroOutUncounted: async (stockTakingId: string) => {
    const res = await operatorApiFetch(`stock_takings/${stockTakingId}/zero_out_uncounted`, {
      method: 'POST',
    });
    return parseApiResponse(res, apiSuccess(ZeroOutSchema));
  },

  // 虛擬倉建單精靈：預覽符合 filter 的 SKU（keyset pagination）
  previewVirtualScope: async ({ merchantId, filter, pageSize, sinceId }: VirtualScopePreviewInput) => {
    const res = await operatorApiFetch('stock_takings/virtual_preview', {
      method: 'POST',
      body: JSON.stringify({
        merchant_id: merchantId,
        filter: filter || {},
        page_size: pageSize,
        since_id: sinceId,
      }),
    });
    return parseApiResponse(res, apiList(VirtualPreviewRowSchema));
  },

  // 虛擬倉建單：以 preview 累積的 inventory_ids 產生一張 StockTaking。
  generateForVirtual: async ({ merchantId, inventoryIds }: { merchantId: string; inventoryIds: string[] }) => {
    const res = await operatorApiFetch('stock_takings/generate_for_virtual', {
      method: 'POST',
      body: JSON.stringify({
        merchant_id: merchantId,
        inventory_ids: inventoryIds,
      }),
    });
    return parseApiResponse(res, apiSuccess(GenerateForVirtualSchema));
  },
};

export default stockTakingService;
export type { ApiErrorEnvelope };
